import { useState } from 'react';

import { DRAWER_DISCARD_PROMPT_MAX_WIDTH } from '@web/constants/app';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';

type GuardCloseArgs = {
  isDirty: boolean;
  canSave: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
};

export type DrawerEditTracker = {
  isEdited: () => boolean;
  track: (isEdited: (() => boolean) | null) => void;
};

type UseDrawerCloseGuardReturn = {
  guardClose: (args: GuardCloseArgs) => void;
  editTracker: DrawerEditTracker;
};

/** Shared close for a drawer's Cancel, X, Esc and outside-click: closes a clean form, confirms
 * before discarding edits, ignores requests mid-save. `editTracker.isEdited()` reports the edits. */
export const useDrawerCloseGuard = (): UseDrawerCloseGuardReturn => {
  const confirm = useConfirmDialog();
  const [editTracker] = useState((): DrawerEditTracker => {
    let tracked: (() => boolean) | null = null;
    return {
      isEdited: () => tracked?.() ?? false,
      track: (isEdited): void => {
        tracked = isEdited;
      },
    };
  });

  const guardClose = ({ isDirty, canSave, isSaving, onSave, onDiscard }: GuardCloseArgs): void => {
    if (isSaving) return;

    if (!isDirty) {
      onDiscard();
      return;
    }

    confirm.alert({
      title: 'Discard unsaved changes?',
      description: 'You have unsaved changes that will be lost if you close this drawer.',
      maxWidth: canSave ? DRAWER_DISCARD_PROMPT_MAX_WIDTH : undefined,
      actions: [
        { label: 'Discard', variant: 'slab-secondary', onClick: onDiscard },
        { label: 'Keep editing', variant: canSave ? 'slab-secondary' : 'slab-primary' },
        ...(canSave ? [{ label: 'Save', variant: 'slab-primary' as const, onClick: onSave }] : []),
      ],
    });
  };

  return { guardClose, editTracker };
};
